import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { Cell, ICell } from '.'
import { AddressCell } from './address'
import { ArrayCell } from './array'
import { AttachmentCell } from './attachment'
import { BooleanCell } from './boolean'
import { ColorCell } from './color'
import { CoordinatesCell } from './coordinates'
import { DateTimeCell } from './dateTime'
import { EmailCell } from './email'
import { IconCell } from './icon'
import { ImageCell } from './image'
import { ImageArrayCell } from './imageArray'
import { NumberCell } from './number'
import { PhoneCell } from './phone'
import { RatingCell } from './rating'
import { RichTextCell } from './richText'
import { TextCell } from './text'
import { URLCell } from './url'

export interface ISingleCellColumnSettings {
  tblId: string
  colId: string
  rowOptions: 'first' | 'last' | 'random' | 'from-start' | 'from-end'
  row: string
}

export interface ISingleCellColumn extends ITableColumn {
  Settings: ISingleCellColumnSettings
}

export interface ISingleCellCell extends ICell {
  value: ICell
  toString: () => string
  settings: ISingleCellColumnSettings
  resolvedType: ColumnTypes
  resolvedSettings: Record<string, any>
}

/**
 * @special this single-cell stored another cell as a value
 */
export class SingleCellCell implements ISingleCellCell {
  value: ICell
  settings: ISingleCellColumnSettings
  resolvedType: ColumnTypes
  resolvedSettings: Record<string, any>
  timezone?: string

  constructor(
    value: Cell,
    settings: ISingleCellColumnSettings,
    resolvedType?: ColumnTypes,
    resolvedSettings?: Record<string, any>,
    timezone?: string
  ) {
    this.settings = settings
    this.resolvedType = resolvedType ?? ColumnTypes.Text
    this.resolvedSettings = resolvedSettings ?? {}
    this.timezone = timezone
    this.value = this.resolveCell(value, this.resolvedType, this.resolvedSettings)
  }

  resolveCell(value: any, type: ColumnTypes, settings: Record<string, any>): any {
    this.resolvedType = type
    this.resolvedSettings = settings

    switch (type) {
      case ColumnTypes.Number:
        return new NumberCell(value, settings as any)
      case ColumnTypes.DateTime:
        return new DateTimeCell(value, settings as any, this.timezone)
      case ColumnTypes.Boolean:
        return new BooleanCell(value, null)
      case ColumnTypes.Address:
        return new AddressCell(value, settings as any)
      case ColumnTypes.Array:
        return new ArrayCell(value, null)
      case ColumnTypes.Image:
        return new ImageCell(value, settings as any)
      case ColumnTypes.Attachment:
        return new AttachmentCell(value, null)
      case ColumnTypes.Coordinate:
        return new CoordinatesCell(value, null)
      case ColumnTypes.Rating:
        return new RatingCell(value, settings as any)
      case ColumnTypes.Icon:
        return new IconCell(value, settings as any)
      case ColumnTypes.ImageArray:
        return new ImageArrayCell(value, null)
      case ColumnTypes.URL:
        return new URLCell(value, null)
      case ColumnTypes.Email:
        return new EmailCell(value, null)
      case ColumnTypes.Phone:
        return new PhoneCell(value, null)
      case ColumnTypes.RichText:
        return new RichTextCell(value, null)
      case ColumnTypes.Color:
        return new ColorCell(value, settings as any)
      // todo: barcode & qr-code
      //
      default:
        return new TextCell(value, null)
    }
  }

  toString(): string {
    return this.value !== null && this.value !== undefined ? this.value.toString() : ''
  }

  toCell<T extends Cell>(value: T): ICell {
    return this
  }
}
